import { MsalProvider } from '@azure/msal-react'
import { DateFnsLocaleProvider } from '@if/core/date'
import i18n, { initializeI18N } from '@if/core/i18n'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { createRoot } from 'react-dom/client'
import { I18nextProvider } from 'react-i18next'
import Router from './containers/Router'
import ConflictHandler from './providers/ConflictHandler'
import GlobalLoaderProvider from './providers/GlobalLoaderProvider'
import LocaleProvider from './providers/LocaleProvider'
import NotificationProvider from './providers/NotificationProvider'
import ThemeProvider from './providers/ThemeProvider'
import UserProvider from './providers/UserProvider'
import initializeAppInsights from './services/AppInsights'
import initializeMsal from './services/Authentification'
import { reportErrorToConsole, setupTracking } from './services/error-logger'
import { AppConfigProvider } from './providers/AppConfigProvider'

const ai = initializeAppInsights()
setupTracking(ai)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      gcTime: Infinity,
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
})
const container = document.getElementById('root')
const root = createRoot(container!)
const msalInstance = initializeMsal()

initializeI18N()
  .then(() => {
    root.render(
      <I18nextProvider i18n={i18n}>
        <MsalProvider instance={msalInstance}>
          <QueryClientProvider client={queryClient}>
            <ThemeProvider>
              <DateFnsLocaleProvider>
                <LocaleProvider>
                  <NotificationProvider>
                    <ConflictHandler>
                      <UserProvider>
                        <GlobalLoaderProvider>
                          <AppConfigProvider>
                            <Router />
                            <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
                          </AppConfigProvider>
                        </GlobalLoaderProvider>
                      </UserProvider>
                    </ConflictHandler>
                  </NotificationProvider>
                </LocaleProvider>
              </DateFnsLocaleProvider>
            </ThemeProvider>
          </QueryClientProvider>
        </MsalProvider>
      </I18nextProvider>,
    )
  })
  .catch(reportErrorToConsole)
